class ShareLink {

	/**
	 * Main modal container
	 */
	public container: HTMLElement;

	/**
	 *
	 * @private
	 */
	private collection: CollectionData;

	/**
	 * Public site share url
	 * @private
	 */
	private readonly publicURL: string;

	/**
	 *
	 * @private
	 */
	private modal: any;

	/**
	 * Get modalBody element so we can add necessary classes depending on the view
	 */
	private modalBodyEl: Element | null = null;

	/**
	 * List of all of our share links
	 * @private
	 */
	private shareLinks: Array<any> = null;

	/**
	 * All footer buttons of the modal
	 * @private
	 */
	private footerButtons: any;

	/**
	 * True if the anonymous role is in the collection acl
	 * @private
	 */
	private hasAnonymous: boolean;

	/**
	 * True if the user has the admin permission to create share links
	 * They will still be able to view the modal so they can see the site share link
	 * @private
	 */
	private hasPermission: boolean;

	/**
	 * Smart collection id
	 * @private
	 */
	private readonly smartId: number;

	/**
	 * Number of invalid items
	 * @private
	 */
	private numInvalid: number;

	/**
	 * All the details for the see more issues part of the modal
	 * @private
	 */
	private invalidDetails: any = {
		loaded: false,
		canEdit: {
			active: true,
			loadedAll: 0,
			page: 0,
			items: []
		},
		cantEdit: {
			active: false,
			loadedAll: 0,
			page: 0,
			items: []
		}
	};

	/**
	 * Acl role for the anonymous user
	 * @private
	 */
	private anonRole: any;

	/**
	 * Tabs for share issues
	 * @private
	 */
	private shareIssueEls: any = {
		canHeader: null,
		canBody: null,
		cantHeader: null,
		cantBody: null
	};

	/**
	 * All requests
	 * @private
	 */
	private requests: Array<XMLHttpRequest>;

	/**
	 * Are we currently processing items to be fixed (have anon in the acl)
	 * @private
	 */
	private processing: boolean = false;


	/**
	 *
	 * @param collection
	 * @param publicURL
	 * @param smartId
	 */
	constructor(collection: any, publicURL: string, smartId: number) {
		this.collection = collection;
		this.publicURL = publicURL;
		this.smartId = smartId;
		this.requests = [];

		this.footerButtons = {
			createLink: {
				id: 'link-generate',
				label: iwi18n.gettext(undefined, 'Create'),
				click: null
			},
			goBack: {
				id: 'show-links',
				label: iwi18n.gettext(undefined, 'Go back'),
				click: () => this.createModalBody(),
				primary: false,
				classStr: 'modal-btn'
			},
			fixItems: {
				id: 'batch-edit-selection',
				label: iwi18n.gettext(undefined, 'Make shareable'),
				click: () => this.fixItems(),
			}
		};
		this.createButton();
	}

	/**
	 * Create share button that opens modal
	 * @private
	 */
	private createButton() {
		this.container = iwUI.createEl('div', {class: 'c-share'});
		let shareBtn = iwUI.createEl('button', {class: 'c-share-btn iw-icon-view btn btn--main'}),
		// Adding a span stops misalignment issues when using custom fonts
		shareText = iwUI.createEl('span', {class: 'c-share-btn-txt'});

		shareText.textContent = iwi18n.gettext(undefined, 'Share links');
		EVENT.attach(this.container, 'click', () => this.createModal());
		shareBtn.append(shareText);

		this.container.append(shareBtn);
	}

	/**
	 * Create the modal shell and fetch links
	 * @private
	 */
	public createModal() {
		this.modal = iwModal.create(this.container, 'share-collection', {
			title: iwi18n.gettext(undefined, 'Share %1', iw.collectionsName),
			closeFunction: () => this.destroy(),
		});

		this.getShareLinks();
	}

	/**
	 * Get our share links
	 * @private
	 */
	private getShareLinks() {
		iwCore.ajax({
			type: 'GET',
			url: '/collections/getsharedlinks',
			data: {
				collectionID: this.collection.id
			},
			success: (e: any) => {
				if (e.success) {
					this.shareLinks = e.data ?? [];
					this.hasAnonymous = e.hasAnonymous;
					this.hasPermission = e.hasPermission;
					this.numInvalid = e.numInvalid;
					this.anonRole = e.anonRole;

					this.createModalBody();
				} else {
					this.modal.setError({msg: e.error ?? iwi18n.gettext(undefined, 'Something went wrong, please try again later')});
				}
			},
			error: (e: any) => this.modal.setError({msg: e.error ?? iwi18n.gettext(undefined, 'Something went wrong, please try again later')})
		});
	}

	/**
	 * Create the modal body
	 * @private
	 */
	private createModalBody() {
		this.destroy();

		let container = document.createDocumentFragment(),
			header = iwUI.createEl('DIV', {class: 'modal-stretch-section modal-stretch-section--title modal-stretch-section--openAccess'}),
			headerActions = iwUI.createEl('DIV', {class: 'modal-actions'}),
			headerInner = iwUI.createEl('DIV', {class: 'modal-stretch-inner'}),
			headerText = iwUI.createEl('H2', {class: 'modal-stretch-title'}),
			siteShareInfo = iwUI.createEl('P', {class: 'modal-info-msg'}),
			newShareLinkBtn = iwUI.createEl('button', {class: 'modal-btn modal-btn--primary iw-icon-plus'}),
			body = iwUI.createEl('DIV', {class: 'modal-stretch-body'}),
			siteShareBody = iwUI.createEl('DIV', {class: 'modal-list'}),
			siteShareItem = iwUI.createEl('DIV', {class: 'modal-list__entry modal-list__entry--bg'}),
			siteShareItemHead = iwUI.createEl('DIV', {class: 'modal-list__entry__head'}),
			siteShareItemFooter = iwUI.createEl('DIV', {class: 'modal-list__entry__footer'}),
			siteShareItemExpire = iwUI.createEl('P', {class: 'modal-txt iw-icon-calendar'}),
			siteShareItemLbl = iwUI.createEl('A', {class: 'modal-link'}),
			errorEl = iwUI.createEl('P', {class: 'modal-error-msg'}),
			siteShareBtn = iwUI.createEl('BUTTON', {class: 'modal-btn-icon modal-btn-icon--txt iw-icon-copy'});

		this.modalBodyEl = document.getElementsByClassName('modal-body')[0];

		siteShareBtn.textContent = iwi18n.gettext(undefined,'Copy link');
		siteShareItemLbl.textContent = iwi18n.gettext(undefined, 'Share this %1 with other users of %2 only', iw.collectionsName, decodeURIComponent(iw.siteTitle));
		siteShareItemExpire.textContent= iwi18n.gettext(undefined, 'Never expires');
		EVENT.attach(siteShareBtn, 'click', () => iwCore.copyToClipboard(this.publicURL));
		EVENT.attach(siteShareItemLbl, 'click', () => iwCore.copyToClipboard(this.publicURL));
		siteShareItemHead.append(siteShareItemLbl, siteShareBtn);
		siteShareItemFooter.append(siteShareInfo, siteShareItemExpire);
		siteShareItem.append(siteShareItemHead, siteShareItemFooter);
		headerText.textContent = iwi18n.gettext(undefined, 'Site access');
		siteShareInfo.textContent = iwi18n.gettext(undefined, 'Sign in may be required. Uses %1 access settings.', iw.collectionsName);
		headerInner.append(headerText);
		header.append(headerInner);
		headerText.textContent = iwi18n.gettext(undefined, 'Share this %1', iw.collectionsName);
		headerInner.append(headerText);
		header.append(headerInner);
		siteShareBody.append(siteShareItem);
		body.append(siteShareBody);

		if (this.hasPermission) {
			if (!this.hasAnonymous && !this.smartId) {
				newShareLinkBtn.setAttribute('disabled', 'disabled');
			}

			headerText.textContent = iwi18n.gettext(undefined, 'Create share links for this %1', iw.collectionsName);
			headerInner.append(headerText);
			header.append(headerInner);

			if (this.hasAnonymous) {
				let canShareText = this.createCanShareText();
				body.append(canShareText);

				if (this.shareLinks.length) {
					this.shareLinks.forEach((f: any) => {
						let cont = iwUI.createEl('DIV', {class: 'modal-list__entry'}),
							linkFooter = iwUI.createEl('DIV', {class: 'modal-list__entry__footer'}),
							downloadAllowed = iwUI.createEl('DD', {class: 'modal-keyVal-value'}),
							downloadAllowedIcon = iwUI.createEl('DT', {class: 'modal-keyVal-key iw-icon-download'}),
							linkHead = iwUI.createEl('DIV', {class: 'modal-list__entry__head'}),
							linkBody = iwUI.createEl('DIV', {class: 'modal-list__entry__body'}),
							linkActions = iwUI.createEl('DIV', {class: 'modal-list__entry__actions'}),
							linkInfoContainer = iwUI.createEl('DL', {class: 'modal-keyVal modal-keyVal--spaceBetween '}),
							linkBodyInfo = iwUI.createEl('DL', {class: 'modal-keyVal-section'}),
							linkViewsSection = iwUI.createEl('DIV', {class: 'modal-keyVal-section'}),
							linkDateSection = iwUI.createEl('DIV', {class: 'modal-keyVal-section modal-keyVal-section--shareExpires'}),
							linkAuthorContainer = iwUI.createEl('DIV', {class: 'modal-list__entry__aside'}),
							linkAuthor = iwUI.createEl('P', {class: 'modal-owner-icon'}),
							name = iwUI.createEl('A', {
								class: 'modal-link',
								href: f.url,
								target: '_blank'
							}),
							expiryPre = iwUI.createEl('DT', {class: 'modal-keyVal-key iw-icon-calendar'}),
							expiry = iwUI.createEl('DD', {class: 'modal-keyVal-value'}),
							viewsPre = iwUI.createEl('DT', {class: 'modal-keyVal-key iw-icon-eye-open'}),
							views = iwUI.createEl('DD', {class: 'modal-keyVal-value'}),
							copy = iwUI.createEl('button', {class: 'modal-btn-icon modal-btn-icon--txt iw-icon-copy'}),
							deleteBtn = iwUI.createEl('button', {class: 'modal-btn-icon modal-btn-icon--danger iw-icon-remove iw_tool_tip_link'});

						name.textContent = f.name;
						copy.textContent = iwi18n.gettext(undefined,'Copy link');
						expiry.textContent = f.dates;
						viewsPre.textContent = iwi18n.gettext(undefined, 'Views:') + ' ';
						views.textContent = f.views;
						EVENT.attach(copy, 'click', () => iwCore.copyToClipboard(f.url));
						EVENT.attach(deleteBtn, 'click', () => this.deleteLink(f.guid), 'return false;');
						expiryPre.setAttribute('aria-label', iwi18n.gettext(undefined, 'Expires:'));
						linkAuthor.textContent = f.user_email.substring(0, 2);
						linkViewsSection.append(viewsPre, views);
						linkDateSection.append(expiryPre, expiry);
						linkAuthorContainer.append(linkAuthor);
						linkInfoContainer.append(linkViewsSection, linkDateSection);
						linkFooter.append(linkAuthorContainer, linkInfoContainer);
						linkActions.append(copy, deleteBtn);
						linkHead.append(name, linkActions);

						if (f.download) {
							downloadAllowed.textContent = iwi18n.gettext(undefined, 'Download enabled');
							linkBodyInfo.append(downloadAllowedIcon, downloadAllowed);
							linkBody.append(linkBodyInfo);
							cont.append(linkAuthorContainer, linkHead, linkBody, linkFooter);
						} else {
							cont.append(linkAuthorContainer, linkHead, linkFooter);
						}
						body.append(cont);
					});

				} else {
					let noLinks = iwUI.createEl('P', {class: 'modal-info-msg'});
					noLinks.textContent = iwi18n.gettext(undefined, 'This %1 is currently not shared outside of %2', iw.collectionsName, decodeURIComponent(iw.siteTitle));
					body.prepend(noLinks);
				}

				newShareLinkBtn.textContent = iwi18n.gettext(undefined, 'New share link');
				EVENT.attach(newShareLinkBtn, 'click', () => this.createNew());
				headerActions.append(newShareLinkBtn);
				header.append(headerActions);

			} else {
				errorEl.textContent = iwi18n.gettext(undefined, 'Unable to share this %1 outside of %2. "Anonymous" not present in the access control list.', iw.collectionsName, decodeURIComponent(iw.siteTitle));
				body.append(errorEl);
			}
		}

		container.append(header, body);
		this.clearFooterBtns();
		this.modal.replaceBody(container);
		iwCore.removeClassName(this.modalBodyEl, ' modal-body--switchTabs');
		iwToolTips.reInit();
	}

	/**
	 * Create the view for creating a new open access link
	 * @private
	 */
	private createNew() {
		let container = document.createDocumentFragment(),
			header = iwUI.createEl('DIV', {class: 'modal-stretch-section modal-stretch-section--title'}),
			headerInner = iwUI.createEl('div', {class: 'modal-stretch-inner'}),
			createLinksContainer = iwUI.createEl('div', {class: 'modal-stretch-body'}),
			headerTxt = iwUI.createEl('h2', {class: 'modal-stretch-title'}),
			name = iwUI.modalTextInput('link-name', iwi18n.gettext(undefined, 'Name the link'), {
				required: true,
				maxlength: 50,
			}, iwi18n.gettext(undefined, 'Set a title for this link for internal notes e.g. Jayne\'s review access.  This is a mandatory field.')),
			nameInp = name.getElementsByTagName('input')[0],
			sunriseSunsetOpt = iwUI.formCheckboxInput('link-sunrise-sunset',
				iwi18n.gettext(undefined, 'Sunrise / sunset dates'),
				{classPrefix: 'modal-', onchange: () => iwCore.slideElement('sunrise-sunset-opts')},
				iwi18n.gettext(undefined, 'Control when your link will be usable.')),
			includeSunriseSunset = sunriseSunsetOpt.getElementsByTagName('input')[0],
			sunriseSunsetDateCont = iwUI.createEl('div', {class: 'iwSlideSection', id: 'sunrise-sunset-opts'}),
			tomorrow = new Date().setDate(new Date().getDate() + 1),
			tomorrowDate = new Date(new Date(tomorrow).setHours(0,0,0,0)),
			sunriseOpts: any = {
				rome: null,
				inGroup: true,
				placeholder: iwi18n.gettext(undefined, 'Now'),
				inputIcon: 'iw-icon-calendar',
				extensionIcon: 'iw-icon-close',
				button: true,
				minStart: tomorrowDate,
				buttonCb: (input: HTMLInputElement) => {
					input.value = '';
				},
				attached: true
			},
			sunrise = iwUI.modalDateTimeInput('sunrise-sunset',
				'date',
				iwi18n.gettext(undefined, 'Active from'),
				sunriseOpts,
				iwi18n.gettext(undefined, 'The embed code or URL will be active from the specified date. "Active from" is the date it will become active. Leave this field blank to activate immediately.')),
			sunsetOpts: any = {
				rome: null,
				inGroup: true,
				placeholder: iwi18n.gettext(undefined, 'Never'),
				inputIcon: 'iw-icon-calendar',
				extensionIcon: 'iw-icon-close',
				button: true,
				minStart: tomorrowDate,
				buttonCb: (input: HTMLInputElement) => {
					input.value = '';
				},
				attached: true
			},
			sunset = iwUI.modalDateTimeInput('sunrise-sunset',
				'date',
				iwi18n.gettext(undefined, 'Expires'),
				sunsetOpts,
				iwi18n.gettext(undefined, 'The embed code or URL will expire from 00:00 on the specified date. "Expires on" is the date it will no longer work. Leave this field blank for it to remain active indefinitely.')),
			forbidDownloadsOpt = iwUI.formCheckboxInput('link-forbid-download',
				iwi18n.gettext(undefined, 'Forbid downloads'),
				{classPrefix: 'modal-',  checked: true},
				iwi18n.gettext(undefined, 'Forbid downloads when viewed via open access.')),
			forbidDownloadsInp = forbidDownloadsOpt.getElementsByTagName('input')[0],
			sunriseSunsetGrp = iwUI.pairInputs(sunrise, sunset),
			getTime = (rome: any) => {
				if (includeSunriseSunset.checked) {
					let date = rome.getDate();
					return date ? date.getTime() / 1000 : null;
				}

				return null;
			},
			canShareText = this.createCanShareText(false);

		iwCore.removeClassName(this.modalBodyEl, ' modal-body--switchTabs');
		headerTxt.textContent = iwi18n.gettext(undefined, 'Create an open access link for this %1', iw.collectionsName);

		sunriseSunsetDateCont.append(sunriseSunsetGrp);

		headerInner.append(headerTxt);
		header.append(headerInner);

		if (canShareText) {
			createLinksContainer.append(canShareText);
		}

		createLinksContainer.append(name, sunriseSunsetOpt, sunriseSunsetDateCont, forbidDownloadsOpt);

		container.append(header, createLinksContainer);

		this.footerButtons.createLink.click = () => this.createLink({
			name: nameInp.value,
			activeFrom: getTime(sunriseOpts.rome),
			expiresOn: getTime(sunsetOpts.rome),
			download: !forbidDownloadsInp.checked
		});
		this.footerButtons.goBack.click = () => this.createModalBody();

		this.clearFooterBtns();
		this.modal.addFooterButtons([this.footerButtons.createLink, this.footerButtons.goBack]);
		this.modal.replaceBody(container);
	}

	/**
	 * Create a new link
	 * @param data
	 * @private
	 */
	private createLink(data: any) {
		if (data.name.length) {
			iwCore.ajax({
				type: 'POST',
				url: '/collections/createsharedlink',
				data: {
					...data,
					collectionID: this.collection.id
				},
				beforeSend: () => iwCore.loading(true),
				complete: () => iwCore.loading(false),
				success: (e: any) => {
					if (e.success) {
						this.shareLinks.unshift(e.link);
						this.createModalBody();
					} else {
						this.modal.setError({msg: iwi18n.gettext(undefined, 'Error creating share link, please try again later.')});
					}
				},
				error: () => this.modal.setError({msg: iwi18n.gettext(undefined, 'Error creating share link, please try again later.')})
			});
		} else {
			this.modal.setError({msg: iwi18n.gettext(undefined, 'Name is required.'), fadeTime: 5000});
		}
	}

	/**
	 * Delete a link
	 * @param guid
	 * @private
	 */
	private deleteLink(guid: string) {
		iwCore.ajax({
			type: 'POST',
			url: '/collections/deletesharedlink',
			data: {
				guid: guid
			},
			beforeSend: () => iwCore.loading(true),
			complete: () => iwCore.loading(false),
			success: (e: any) => {
				if (e.success) {
					this.shareLinks = this.shareLinks.filter((f: any) => f.guid !== guid);
					this.createModalBody();
				} else {
					this.modal.setError({msg: iwi18n.gettext(undefined, 'Error deleting share link, please try again later.')});
				}
			},
			error: () => this.modal.setError({msg: iwi18n.gettext(undefined, 'Error deleting share link, please try again later.')})
		});
	}

	/**
	 * Creates the warning label if all of the collection items have anon or not
	 * @private
	 */
	private createCanShareText(showProcessing: boolean = true) {
		let infoTxt = iwUI.createEl('p', {class: 'modal-alert-msg'});

		if (showProcessing) {
			if (this.processing) {
				infoTxt.textContent = iwi18n.gettext(undefined, 'Processing items…');
				setTimeout(() => {
					this.processing = false;
					infoTxt.textContent = '';
				}, 5000);
			} else if (this.numInvalid) {
				let infoLink = iwUI.createEl('A', {class: 'modal-link modal-txt--bold'});

				infoTxt.textContent = iwi18n.ngettext(undefined,
					'%2 item in this %3 is currently not shareable outside of this site.',
					'%2 items in this %3 are currently not shareable outside of this site.',
					this.numInvalid, this.numInvalid.toString(), iw.collectionsName) + ' ';


				infoLink.textContent = iwi18n.gettext(undefined, 'See share issues');
				EVENT.attach(infoLink, 'click', () => this.createResolveSection(() => this.createModalBody()));

				infoTxt.append(infoLink);
			} else if (this.shareLinks.length) {
				infoTxt.className = 'modal-info-msg';
				infoTxt.textContent = iwi18n.gettext(undefined, 'Users outside of %1 can access all items in this %2 using the link(s) below.', decodeURIComponent(iw.siteTitle), iw.collectionsName);
			}

			return infoTxt;
		}

		return null;
	}

	/**
	 *
	 * @private
	 */
	private createResolveSection(goBackFn: () => void) {
		this.shareIssueEls.canHeader = iwUI.createEl('div', {class: 'modal-tab-title is-active hide'});
		this.shareIssueEls.cantHeader = iwUI.createEl('div', {class: 'modal-tab-title hide'});

		this.shareIssueEls.canBody = iwUI.createEl('div', {class: 'modal-tab-content is-active'});
		this.shareIssueEls.cantBody = iwUI.createEl('div', {class: 'modal-tab-content'});

		let container = document.createDocumentFragment(),
			headerContainer = iwUI.createEl('div', {class: 'modal-tab-header'}),
			bodyContainer = iwUI.createEl('div', {class: 'modal-tab-body'}),
			canMsg = iwUI.createEl('p', {class: 'modal-alert-msg'}),
			cantMsg = iwUI.createEl('p', {class: 'modal-alert-msg'}),

		scrollStop = (e: Event, type: string) => {
			let el = <HTMLElement>e.currentTarget;
			if (!this.invalidDetails[type].loadedAll && el.scrollHeight - el.scrollTop - el.clientHeight <= 1) {
				this.loadMoreInvalidItems(type === 'canEdit');
			}
		};

		this.shareIssueEls.canHeader.textContent = iwi18n.gettext(undefined, 'Make shareable');
		this.shareIssueEls.cantHeader.textContent = iwi18n.gettext(undefined, 'Sharing denied');

		canMsg.textContent = iwi18n.gettext(undefined, 'The following items have been configured to disallow sharing. Would you like to make them shareable?');
		cantMsg.textContent = iwi18n.gettext(undefined, 'The following items have been configured to disallow sharing, and your Administrator has not given you permission to change this.');

		EVENT.attach(this.shareIssueEls.canHeader, 'click', () => this.switchTabs(this.shareIssueEls.canHeader, this.shareIssueEls.canBody, false));
		EVENT.attach(this.shareIssueEls.cantHeader, 'click', () => this.switchTabs(this.shareIssueEls.cantHeader, this.shareIssueEls.cantBody, true));

		EVENT.attach(this.shareIssueEls.canBody, 'scroll', (e: Event) => scrollStop(e, 'canEdit'), false);
		EVENT.attach(this.shareIssueEls.cantBody, 'scroll', (e: Event) => scrollStop(e, 'cantEdit'), false);

		this.shareIssueEls.canBody.append(canMsg);
		this.shareIssueEls.cantBody.append(cantMsg);

		if (this.invalidDetails.loaded) {
			this.addItems(this.invalidDetails.canEdit.items, this.invalidDetails.cantEdit.items);
		} else {
			this.loadMoreInvalidItems(true);
		}

		headerContainer.append(this.shareIssueEls.canHeader, this.shareIssueEls.cantHeader);
		bodyContainer.append(this.shareIssueEls.canBody, this.shareIssueEls.cantBody);
		container.append(headerContainer, bodyContainer);

		this.clearFooterBtns();

		this.footerButtons.goBack.click = goBackFn;
		this.modal.addFooterButtons([this.footerButtons.fixItems, this.footerButtons.goBack]);
		this.modal.replaceBody(container);

		iwCore.addClassName(this.modalBodyEl, 'modal-body--switchTabs');
	}

	/**
	 *
	 * @param tab
	 * @param body
	 * @param disableFixBtn
	 */
	public switchTabs(tab: HTMLElement, body: HTMLElement, disableFixBtn: boolean) {
		let elItems = [this.shareIssueEls.canHeader, this.shareIssueEls.cantHeader, this.shareIssueEls.canBody, this.shareIssueEls.cantBody];

		iwCore.massClass(iwCore.removeClassName, elItems, 'is-active')
		iwCore.massClass(iwCore.addClassName, [tab, body], 'is-active');

		if (disableFixBtn) {
			this.invalidDetails.canEdit.active = false;
			this.invalidDetails.cantEdit.active = true;
			this.modal.disableFooterButtons([this.footerButtons.fixItems.id]);
		} else {
			this.invalidDetails.canEdit.active = true;
			this.invalidDetails.cantEdit.active = false;
			this.modal.enableFooterButtons([this.footerButtons.fixItems.id]);
		}
	}

	/**
	 * Add invalid items to issues section of modal
	 * @param canEditItems
	 * @param cantEditItems
	 * @private
	 */
	private addItems(canEditItems: Array<any>, cantEditItems: Array<any>) {
		let createItem = (f: any) => {
			let item = iwUI.createEl('div', {class: 'modal-stretch-section modal-stretch-section--collectionItem'}),
				thumbLnk = iwUI.createEl('A', {class: 'modal-collection-thumbLnk'}),
				thumb = iwUI.createEl('img', {
					class: 'modal-collection-img',
					src: f.Fields.thumbnail ?? '',
					onerror: `this.src='${iwUI.imageFallback(f.Fields.mediaType, '')}'`
				}),
				info = iwUI.createEl('div', {class: 'modal-collection-info'}),
				title = iwUI.createEl('P', {class: 'modal-txt modal-txt--bold'}),
				date;

			title.textContent = f.fieldClasses.title;
			thumbLnk.append(thumb);

			if (f.fieldClasses.date !== undefined) {
				date = iwUI.createEl('div', {class: 'modal-txt iw-icon-calendar'});
				date.textContent = f.fieldClasses.date;
				info.append(date);
			}

			info.insertBefore(title, info.firstChild);
			item.append(thumbLnk, info);
			return item;
		}

		if (canEditItems && canEditItems.length) {
			canEditItems.forEach((f: any) => this.shareIssueEls.canBody.append(createItem(f)));
			this.shareIssueEls.canHeader.classList.remove('hide');
		} else if (cantEditItems && cantEditItems.length) {
			cantEditItems.forEach((f: any) => this.shareIssueEls.cantBody.append(createItem(f)));
			this.shareIssueEls.cantHeader.classList.remove('hide');

			if (!this.invalidDetails.canEdit.items.length) {
				this.switchTabs(this.shareIssueEls.cantHeader, this.shareIssueEls.cantBody, true)
			}
		}
	}

	/**
	 * Gets more invalid items
	 * @private
	 */
	private loadMoreInvalidItems(canEdit: boolean) {
		this.requests.push(iwCore.ajax({
			type: 'GET',
			url: '/collections/getinvaliditems',
			data: {
				id: this.collection.id,
				page: this.invalidDetails.canEdit.active ? this.invalidDetails.canEdit.page++ : this.invalidDetails.cantEdit.page++,
				canEdit: canEdit,
				all: !this.invalidDetails.loaded
			},
			beforeSend: () => iwCore.loading(true),
			complete: () => iwCore.loading(false),
			success: (e: any) => {
				if (e.success) {
					this.invalidDetails.loaded = true;
					// if we got less than 100 items then this is the last round, dont load more
					if (e.canEditItems && !this.invalidDetails.canEdit.loadedAll) {
						this.invalidDetails.canEdit.items = this.invalidDetails.canEdit.items.concat(e.canEditItems);
						this.invalidDetails.canEdit.loadedAll = e.canEditItems.length < 100;
						this.addItems(e.canEditItems, null);
					}

					if (e.cantEditItems && !this.invalidDetails.cantEdit.loadedAll) {
						this.invalidDetails.cantEdit.items = this.invalidDetails.cantEdit.items.concat(e.cantEditItems);
						this.invalidDetails.cantEdit.loadedAll = e.cantEditItems.length < 100;
						this.addItems(null, e.cantEditItems);
					}

				} else {
					this.modal.setError({msg: iwi18n.gettext(undefined, 'There was an error fetching items')});
					this.invalidDetails.canEdit.active ? this.invalidDetails.canEdit.loadedAll = true : this.invalidDetails.cantEdit.loadedAll = true;
				}
			},
			error: (e: any) => {
				if (e.readyState !== 0 && e.readyState !== 4) {
					this.modal.setError({msg: iwi18n.gettext(undefined, 'There was an error fetching items')});
					this.invalidDetails.canEdit.active ? this.invalidDetails.canEdit.loadedAll = true : this.invalidDetails.cantEdit.loadedAll = true;
				}
			}
		}));
	}

	/**
	 * Get collection ids and send ids to metadata save
	 * @private
	 */
	private fixItems() {
		this.modal.disableFooterButtons([this.footerButtons.fixItems.id]);
		CollectionsViewer.getCollectionIds(this.collection.id,
			null,
			null,
			BatchTypes.COLLECTION_SHARE,
			(e: any) => {
				if (e.success) {
					iwCore.ajax({
						type: 'POST',
						dataType: 'json',
						url: '/metadataedit/save',
						data: {
							aclLists: JSON.stringify([{
								type: 'batchRecord',
								acl: [this.anonRole]
							}]),
							batchKey: e.key
						},
						beforeSend: () => iwCore.loading(true),
						complete: () => iwCore.loading(false),
						success: (data: any) => {
							if (data.success) {
								this.processing = true;
								this.getShareLinks();
							} else {
								iwNotifications.error(iwi18n.gettext('projects', 'There was an error saving this information. Please close and try again.'), iwi18n.gettext('projects', 'Error'));
							}
						},
						error: () => iwNotifications.error(iwi18n.gettext('projects', 'There was an error saving this information. Please close and try again.'), iwi18n.gettext('projects', 'Error'))
					});
				} else {
					iwNotifications.error(iwi18n.gettext('projects', 'There was an error retrieving records.'), iwi18n.gettext('projects', 'Error'));
				}
			}, iwi18n.gettext('projects', 'There was an error retrieving records.'));
	}

	/**
	 * Clears all footer buttons
	 * @private
	 */
	private clearFooterBtns() {
		this.modal.removeFooterButtons([this.footerButtons.createLink.id, this.footerButtons.goBack.id, this.footerButtons.fixItems.id]);
	}

	/**
	 * Destroy this object
	 * @private
	 */
	private destroy() {
		this.requests.forEach((f: XMLHttpRequest) => f.abort());
		this.requests = [];

		this.invalidDetails = {
			loaded: false,
			canEdit: {
				active: true,
				loadedAll: 0,
				page: 0,
				items: []
			},
			cantEdit: {
				active: false,
				loadedAll: 0,
				page: 0,
				items: []
			}
		}
	}
}